import { c as _c } from "react/compiler-runtime";
import { cva } from 'class-variance-authority';
import type { FC } from 'react';
import { Button as AntButton } from '@/utils/antd-imports';
import { cn } from '../../../libs/utils';
import type { ButtonProps } from './Button.type';
import { renderIcon } from './renderIcon';

// Определяем варианты кнопки с помощью class-variance-authority
const buttonVariants = cva(
// Базовые стили для всех кнопок
['inline-flex items-center justify-center', 'font-montserrat font-bold uppercase', 'text-center align-middle', 'transition-all duration-200 ease-in-out', 'focus:outline-none focus:ring-2 focus:ring-offset-2', 'disabled:opacity-50 disabled:cursor-not-allowed', 'select-none'], {
  variants: {
    variant: {
      // Градиентная кнопка (основная)
      gradient: ['bg-gradient-to-r from-[#FF4D00] to-[#FF8000]', 'text-white', 'border-0', 'hover:from-[#E63900] hover:to-[#E66F00]', 'focus:ring-orange-500', 'active:from-[#CC2F00] active:to-[#CC5C00]'],
      // Черная кнопка (основная)
      black: ['bg-[#000014]', 'text-white', 'border-2 border-[#000014]', 'hover:bg-[#000014]/60', 'hover:border-[#000014]/60', 'focus:ring-gray-500', 'active:bg-gray-100'],
      // Белая кнопка
      white: ['bg-white', 'text-[#000014]', 'border-2 border-gray-200', 'hover:bg-white/90 hover:border-gray-300', 'focus:ring-gray-500', 'active:bg-gray-100'],
      // Белая кнопка (вторичная)
      whiteSecondary: ['bg-[#E9ECF0]', 'text-[#1E1E2A]', 'border-0', 'hover:bg-white hover:text-[#3D63E8]', 'disabled:bg-[#AEADC899] disabled:text-[#1E1E2ACC] disabled:hover:bg-[#AEADC899 disabled:hover:text-[#1E1E2ACC]'],
      // Белая кнопка (вторичная) с обводкой
      whiteSecondaryOutline: ['bg-transparent', 'text-[#E9ECF0CC]', 'border-2 border-[#E9ECF033]', 'hover:text-[#E9ECF0] hover:border-[#E9ECF066]', 'disabled:border-[#87869633] disabled:text-[#87869666] disabled:hover:border-[#87869633] disabled:hover:text-[#87869666]'],
      // Белая кнопка с обводкой
      whiteOutline: ['bg-transparent', 'text-white', 'border-2 border-white', 'hover:bg-white hover:text-[#191919]', 'focus:ring-white', 'active:bg-gray-100 active:text-[#191919]'],
      // Черная кнопка с обводкой
      blackOutline: ['bg-transparent', 'text-black', 'border-2 border-black', 'hover:bg-black hover:text-white', 'focus:ring-black', 'active:bg-gray-100 active:text-white'],
      // Оранжевая кнопка с обводкой
      outline: ['bg-transparent', 'text-[#FF4D00]', 'border-2 border-[#FF4D00]', 'hover:bg-[#FF4D00] hover:text-white', 'focus:ring-orange-500', 'active:bg-[#E63900]'],
      // Призрачная кнопка
      ghost: ['bg-transparent', 'text-[#FF4D00]', 'border-0', 'hover:bg-[#FF4D00]/10', 'focus:ring-orange-500', 'active:bg-[#FF4D00]/20'],
      purple: ['bg-purple-brand', 'text-white', 'border-0', 'hover:bg-purple-brand-hover', 'focus:ring-purple-500', 'active:bg-purple-brand-hover'],
      warningOutline: ['bg-transparent', 'text-[#F79009]', 'border-2 border-[#F79009]', 'hover:bg-[#F79009] hover:text-white', 'focus:ring-orange-500', 'active:bg-[#F79009] active:text-white'],
      dangerOutline: ['bg-transparent', 'text-[#F04438]', 'border-2 border-[#F04438]', 'hover:bg-[#F04438] hover:text-white', 'focus:ring-orange-500', 'active:bg-[#F04438] active:text-white']
    },
    size: {
      sm: ['h-10', 'px-4', 'text-sm', 'rounded-md', 'tracking-wide'],
      md: ['h-12', 'px-6', 'text-base', 'rounded-[16px]', 'tracking-wide'],
      md_a: ['h-[56px]', 'p-4', 'text-[17px]', 'rounded-[14px]', 'tracking-wide', 'tablet:h-[60px] tablet:text-[19px]'],
      '48_56': ['h-[48px]', 'p-4', 'text-[16px]', 'rounded-[14px]', 'tracking-wide', 'lg:h-[56px]', 'lg:text-[18px]'],
      '2md': ['h-14', 'px-11', 'text-base', 'rounded-[16px]', 'tracking-wide'],
      lg: ['h-[92px]', 'px-16', 'text-[30px]', 'leading-none', 'rounded-[10px]', 'tracking-[0.02em]'],
      // 2md adaptive
      '2md_a': ['h-11', 'text-base', 'px-4', 'lg:h-[60px]', 'rounded-[16px]', 'lg:px-11', 'tracking-wide']
    },
    width: {
      auto: 'w-auto',
      full: 'w-full'
    }
  },
  defaultVariants: {
    variant: 'gradient',
    size: 'md',
    width: 'auto'
  }
});
export const Button: FC<ButtonProps> = t0 => {
  const $ = _c(48);
  let children;
  let className;
  let contentClassName;
  let disabled;
  let leftIcon;
  let props;
  let rightIcon;
  let slots;
  let t1;
  let t2;
  let t3;
  let t4;
  let textClassName;
  let type;
  if ($[0] !== t0) {
    ({
      children,
      textClassName,
      contentClassName,
      leftIcon,
      rightIcon,
      disabled,
      type,
      className,
      slots,
      loading: t1,
      variant: t2,
      size: t3,
      width: t4,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = contentClassName;
    $[4] = disabled;
    $[5] = leftIcon;
    $[6] = props;
    $[7] = rightIcon;
    $[8] = slots;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = textClassName;
    $[14] = type;
  } else {
    children = $[1];
    className = $[2];
    contentClassName = $[3];
    disabled = $[4];
    leftIcon = $[5];
    props = $[6];
    rightIcon = $[7];
    slots = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    textClassName = $[13];
    type = $[14];
  }
  const loading = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "gradient" : t2;
  const size = t3 === undefined ? "md" : t3;
  const width = t4 === undefined ? "auto" : t4;
  const isDisabled = disabled || loading;
  let t5;
  if ($[15] !== className || $[16] !== size || $[17] !== variant || $[18] !== width) {
    t5 = cn(buttonVariants({
      variant,
      size,
      width
    }), className);
    $[15] = className;
    $[16] = size;
    $[17] = variant;
    $[18] = width;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== contentClassName) {
    t6 = cn("inline-flex w-full items-center justify-between", contentClassName);
    $[20] = contentClassName;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== loading || $[23] !== slots) {
    t7 = loading && (slots?.loadIcon ? slots.loadIcon : <span className="inline-flexflex-shrink-0 mr-2 items-center"><svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg></span>);
    $[22] = loading;
    $[23] = slots;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== leftIcon || $[26] !== loading) {
    t8 = leftIcon && !loading && <span className="mr-2 inline-flex flex-shrink-0 items-center">{renderIcon(leftIcon)}</span>;
    $[25] = leftIcon;
    $[26] = loading;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== textClassName) {
    t9 = cn("inline-flex w-full  justify-center ", textClassName);
    $[28] = textClassName;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== children || $[31] !== t9) {
    t10 = <span className={t9}>{children}</span>;
    $[30] = children;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== loading || $[34] !== rightIcon) {
    t11 = rightIcon && !loading && <span className=" ml-2 inline-flex flex-shrink-0">{renderIcon(rightIcon)}</span>;
    $[33] = loading;
    $[34] = rightIcon;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t6 || $[39] !== t7 || $[40] !== t8) {
    t12 = <span className={t6}>{t7}{t8}{t10}{t11}</span>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== isDisabled || $[43] !== props || $[44] !== t12 || $[45] !== t5 || $[46] !== type) {
    t13 = <AntButton type={type} className={t5} disabled={isDisabled} {...props}>{t12}</AntButton>;
    $[42] = isDisabled;
    $[43] = props;
    $[44] = t12;
    $[45] = t5;
    $[46] = type;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  return t13;
};
export type { ButtonProps };